/**
 * The Team page Registration tab's sub-tabs, in switcher order.
 *
 * `command-center` is the Team Budget sheet (the key predates the "Team Budget"
 * label and is deliberately unchanged).
 */
export type RegistrationSubTab = 'command-center' | 'billing' | 'player-finder'

export interface RegistrationSubTabGates {
  /** The balance probe found a Team Budget sheet the caller can see. */
  showCommandCenter: boolean
  /** `permission.update` — the former Billing tab gate. */
  canEditBilling: boolean
  /** The Player Finder probe found a registration whose ad the caller manages. */
  showPlayerFinder: boolean
}

/** The visible sub-tabs, in switcher order. */
export const registrationSubTabs = ({
  showCommandCenter,
  canEditBilling,
  showPlayerFinder,
}: RegistrationSubTabGates): RegistrationSubTab[] => {
  const tabs: RegistrationSubTab[] = []
  if (showCommandCenter) {
    tabs.push('command-center')
  }
  if (canEditBilling) {
    tabs.push('billing')
  }
  if (showPlayerFinder) {
    tabs.push('player-finder')
  }

  return tabs
}

/**
 * The sub-tab to render: the user's pick while it is still visible, otherwise
 * the first visible one — Team Budget, then Billing, then Player Finder.
 *
 * Derived from the CURRENT gates on every render, never frozen at mount: the
 * probes resolve asynchronously and often after another sub-tab has already
 * rendered, and a gate can also turn off under the active selection.
 */
export const resolveRegistrationSubTab = (
  subTabs: readonly RegistrationSubTab[],
  userChoice: RegistrationSubTab | null
): RegistrationSubTab =>
  userChoice && subTabs.includes(userChoice)
    ? userChoice
    : subTabs[0] ?? 'command-center'

/**
 * A `?tab=` deep link that pre-selects a sub-tab: `billing` (a redirected
 * /billing bookmark) or `player-finder`. Anything else follows the default.
 */
export const registrationSubTabFromSearch = (
  search: string
): RegistrationSubTab | null => {
  const requested = new URLSearchParams(search).get('tab')

  return requested === 'billing' || requested === 'player-finder'
    ? requested
    : null
}
